"use client";
import { useEffect, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";

import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { Logo } from "@/assets";
import { Button } from "@/components/ui/button";
import LanguageSwitcher from "@/components/LanguageSwitcher";

// import LanguageSwitcher from "@/app/[lang]/language-switcher";

export const LINKS = [
  {
    label: "Home",
    href: "/",
  },
  { label: "Product", href: "/product" },
  { label: "Pricing", href: "/pricing" },
];

const callToAction = [{ label: "Book a Demo", href: "/book-demo" }];

export default function Navbar({ lang }) {
  const pathname = usePathname();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <nav className="py-6 hidden lg:block">
        <div className="grid grid-cols-3 items-center content">
          <div>
            <Link href="/" aria-label="Home">
              <Logo className="h-10 text-primary" />
            </Link>
          </div>
          <div className="flex justify-center">
            <div className="bg-white rounded-xl flex gap-1 items-center p-2 transition-all duration-300 relative">
              {LINKS.map((link) => {
                const isActive =
                  link.href === "/"
                    ? pathname.replace(`/${lang}`, "") === "" ||
                      pathname.replace(`/${lang}`, "") === "/"
                    : pathname.replace(`/${lang}`, "") === link.href;
                return (
                  <Link
                    key={link.href}
                    // href={`/${lang}${link.href}`}
                    href={link.href}
                    className={cn(
                      "rounded-xl font-medium py-2 px-4 relative z-10 hover:bg-accent",
                      isActive && "text-white"
                    )}
                  >
                    {link.label}
                    {isActive && (
                      <motion.div
                        layoutId="activeNavLink"
                        className="absolute inset-0 bg-primary rounded-xl -z-10"
                        transition={{
                          type: "spring",
                          bounce: 0.2,
                          duration: 0.6,
                        }}
                      />
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
          <div className="flex justify-end items-center gap-6">
            {callToAction.map((action) => (
              <Button
                key={action.href}
                asChild
                showArrow
                trackEvent={action.label}
                trackProps={{
                  page: "Global",
                  section: "Nav",
                }}
              >
                <Link key={action.href} href={action.href}>
                  {action.label}
                </Link>
              </Button>
            ))}
          </div>
        </div>
        <div className="absolute right-2 top-0">
          <LanguageSwitcher align="end" background={true} size="sm" />
        </div>
      </nav>
      <nav className="lg:hidden bg-white py-4 shadow-sm">
        <div className="flex justify-between items-center content">
          <Link href="/" aria-label="Home">
            <Logo className="h-6 text-primary" />
          </Link>
          <div className="flex items-center gap-4">
            <MobileMenu
              buttons={callToAction}
              isOpen={isOpen}
              setIsOpen={setIsOpen}
            />
          </div>
        </div>
      </nav>
    </>
  );
}

const menu = {
  show: {
    clipPath: "ellipse(200% 150% at 100% 0%)",
    transition: {
      type: "spring",
      stiffness: 40,
      restDelta: 2,
      // delay: 0.2,
      delayChildren: 0.2,
      staggerChildren: 0.05,
    },
  },
  hidden: {
    clipPath: "ellipse(50% 25% at 120% -30%)",
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 20,
    },
  },
};

const linkVariants = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      // duration: 0.04,
      // ease: "easeInOut",
      type: "spring",
      stiffness: 75,
      mass: 0.5,
    },
  },
};

function MobileMenu({ buttons, isOpen, setIsOpen }) {
  const handleClick = () => {
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    if (isOpen) {
      document.documentElement.classList.add("prevent-scroll");
      // window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      document.documentElement.classList.remove("prevent-scroll");
    }
    return () => {};
  }, [isOpen]);

  const pathname = usePathname();

  useEffect(() => {
    if (isOpen) {
      setIsOpen(false);
      document.documentElement.classList.remove("prevent-scroll");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const MotionLink = motion.create(Link);
  // const MotionButton = motion.create(Button);
  return (
    <>
      <button
        onClick={handleClick}
        className="flex flex-col justify-center items-center"
      >
        <span
          className={`bg-primary block transition-all duration-300 ease-out h-0.5 w-6 rounded-lg ${
            isOpen ? "rotate-45 translate-y-1" : "-translate-y-[3px]"
          }`}
        ></span>
        <span
          className={`bg-primary block transition-all duration-300 ease-out h-0.5 w-6 rounded-lg my-0.5 ${
            isOpen ? "opacity-0" : "opacity-100"
          }`}
        ></span>
        <span
          className={`bg-primary block transition-all duration-300 ease-out h-0.5 w-6 rounded-lg' ${
            isOpen ? "-rotate-45 -translate-y-1" : "translate-y-[3px]"
          }`}
        ></span>
      </button>
      <motion.div
        className={cn(
          "fixed left-0 z-50 visible w-full overflow-hidden px-6 pt-16 pb-6 mb-12 bg-white flex flex-col justify-between items-center top-[56px] h-[calc(100svh-56px)]",
          isOpen ? "pointer-events-auto" : "pointer-events-none"
        )}
        initial="hidden"
        animate={isOpen ? "show" : "hidden"}
        variants={menu}
      >
        {LINKS.map(({ href, label }, index) => (
          <MotionLink
            key={index}
            href={href || "/"}
            className="text-xl text-primary !font-medium"
            variants={linkVariants}
          >
            {label}
          </MotionLink>
        ))}

        <div className="flex flex-col gap-4 w-full pt-12">
          {buttons.map(({ href, label, onClick }, index) => (
            <motion.div
              key={index + "button"}
              variants={linkVariants}
              onClick={onClick}
            >
              <Button
                asChild
                className="w-full !py-4 h-12"
                trackEvent={label}
                trackProps={{
                  page: "Global",
                  section: "Nav",
                }}
              >
                {href ? (
                  <Link href={href} aria-label={label}>
                    {label}
                  </Link>
                ) : (
                  label
                )}
              </Button>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </>
  );
}
